import {
    DatabaseType,
    DatasetGeometryPoint,
    FilterItem,
} from "@biggeo/bg-server-lib/studio";
import {
    BadgeDot,
    Divider,
    HorizontalScroller,
    LogicOperator,
    Stack,
    Tab,
    tabClasses,
    TabGroup,
    tabGroupClasses,
} from "@biggeo/bg-ui/lab";
import * as A from "fp-ts/lib/Array";
import * as O from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";

import { Initialized } from "@vividtheory/remotedata";
import { useErrorToast } from "../../../toaster/containers/redux/hooks";
import { AuroraClient } from "../../../utils/socket";
import { MapTableTabs } from "../../map-wrappers/MapViewWrapper";
import { useMap } from "../../mapbox/context";
import { SetDatasetContextType } from "../../mapbox/context/utils/general-utils";
import {
    MapContextDataset,
    MapContextDatasetFilter,
} from "../../mapbox/context/types";
import { onFlyToMapFeature } from "../../mapbox/utils/data-hooks-utils";
import { setSelectedRowPulsingDot } from "../../utils/geometry-utils";
import { DataRspRows } from "../../utils/type";
import { DEFAULT_LOGIC_OPERATOR, getOrderedDatasetTable } from "../utils/utils";
import DatasetTableContainer from "./DatasetTableContainer";
import { GeospatialSelection } from "@biggeo/bg-utils";

interface IMainDatasetTableContainer
    extends Pick<SetDatasetContextType, "updateDataset"> {
    readonly tab: MapTableTabs;
    readonly datasets: MapContextDataset[];
    readonly channelId: string;
    readonly geospatialSelection: GeospatialSelection;
    readonly sockets: readonly AuroraClient[];
    readonly hasTransitioned: boolean;
}

export const MainDatasetTableContainer = ({
    tab,
    datasets: d,
    channelId,
    geospatialSelection,
    updateDataset,
    sockets,
    hasTransitioned,
}: IMainDatasetTableContainer) => {
    const errorToast = useErrorToast();

    const { map, isMapLoaded, polygons, dispatch } = useMap();
    const datasets = useMemo(() => getOrderedDatasetTable(d), [d]);
    const [dataset, setDataset] = useState<MapContextDataset>(datasets[0]);

    const tableId = dataset.dataSource.tableId;

    const serverUrl = dataset.dataSourceConfiguration.serverUrl || undefined;

    const client = sockets.find((s) => s.serverUrl === serverUrl);

    const onTabClick = (d: MapContextDataset) => {
        setDataset(d);

        dispatch?.({
            type: "SET_SOCKET_FILTERED_DATA_RESPONSE",
            values: {
                databaseId: d.dataSource.id,
                data: new Initialized(),
            },
        });
    };

    const onGoToClick = (rows: DataRspRows) => {
        onFlyToMapFeature({
            rows,
            map,
            isMapLoaded,
            tableId: tableId ?? undefined,
            geographyColumn: dataset.dataSource.geographyColumn || undefined,
            datasetId: dataset.dataSource.id,
            geographyType: dataset.geographyType,
            onError: () =>
                errorToast(
                    `Cannot fly to point. Longitude & Latitude coordinates or Table Id are missing for dataset #${dataset.dataSource.name}`
                ),
            type: dataset.dataSource.type ?? DatabaseType.point,
        });
    };

    const onDatasetUpdate = (input: {
        currentDataset: MapContextDataset;
        i: Partial<{
            filters: FilterItem[];
            logicOperator: LogicOperator;
            selectedRows: Omit<DatasetGeometryPoint, "fkDataSourceId">[];
            pinnedRows: Omit<DatasetGeometryPoint, "fkDataSourceId">[];
        }>;
    }) => {
        const { currentDataset, i } = input;

        const filters: Partial<MapContextDatasetFilter | undefined> = omitBy(
            {
                logicOperator:
                    i.logicOperator ?? currentDataset.filters.logicOperator,
                filters: i.filters
                    ? pipe(
                          i.filters,
                          A.map((f) => ({
                              ...f,
                              id: uuid(),
                          }))
                      )
                    : currentDataset.filters.filters,
            },
            isNil
        );
        const params = omitBy(
            {
                selectedRows: i.selectedRows
                    ? pipe(
                          i.selectedRows,
                          A.map((r) => ({
                              ...r,
                              fkDataSourceId: currentDataset.dataSource.id,
                              fkMapTemplateDatasetId:
                                  currentDataset.mapTemplateDataset?.id,
                          }))
                      )
                    : undefined,
                pinnedRows: i.pinnedRows
                    ? pipe(
                          i.pinnedRows,
                          A.map((r) => ({
                              ...r,
                              fkDataSourceId: currentDataset.dataSource.id,
                              fkMapTemplateDatasetId:
                                  currentDataset.mapTemplateDataset?.id,
                          }))
                      )
                    : undefined,
            },
            isNil
        );

        updateDataset({
            dataSourceId: currentDataset.dataSource.id,
            dataset: {
                filters,
                ...params,
            },
        });

        if (params.selectedRows && map.current) {
            setTimeout(() => {
                setSelectedRowPulsingDot({
                    action: "add",
                    datasets,
                    map,
                    isMapLoaded,
                    polygons,
                });
            }, 200);
        }

        if (isEqual(currentDataset.dataSource.id, dataset.dataSource.id)) {
            setDataset((p) => ({
                ...p,
                filters: {
                    logicOperator:
                        filters.logicOperator ||
                        dataset.filters?.logicOperator ||
                        DEFAULT_LOGIC_OPERATOR,
                    filters: filters.filters || dataset.filters?.filters || [],
                },
                ...params,
            }));
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const currentDataset = datasets.find((d) =>
            isEqual(d.dataSource.id, dataset.dataSource.id)
        );

        const hasChanged =
            currentDataset?.selectedRows.length !==
                dataset?.selectedRows.length ||
            currentDataset?.pinnedRows.length !== dataset?.pinnedRows.length ||
            currentDataset?.filters.filters.length !==
                dataset?.filters.filters.length;

        if (currentDataset && hasChanged) {
            setDataset({
                ...dataset,
                filters: currentDataset.filters,
                selectedRows: currentDataset.selectedRows,
                pinnedRows: currentDataset.pinnedRows,
            });
        }
        if (!currentDataset) {
            setDataset(datasets[0]);
        }
    }, [datasets]);

    return (
        <Stack height="100%" width="100%">
            <HorizontalScroller>
                <TabGroup
                    value={dataset.dataSource.id}
                    sx={{
                        [`& .${tabGroupClasses["tab-container"]}`]: {
                            gap: 2,
                        },
                        [`& .${tabClasses.root}`]: {
                            padding: 2,
                            gap: 1,
                        },
                        [`& .${tabClasses.default}`]: {
                            borderRadius: 0,
                        },
                    }}
                >
                    {pipe(
                        datasets,
                        A.map((d) => {
                            const filtersCount = pipe(
                                d.filters?.filters,
                                O.fromNullable,
                                O.fold(
                                    () => 0,
                                    (filters) => filters.length
                                )
                            );

                            return (
                                <>
                                    <Tab
                                        key={d.dataSource.id}
                                        value={d.dataSource.id}
                                        onClick={() => {
                                            onTabClick(d);
                                        }}
                                        endNode={
                                            isEqual(
                                                filtersCount,
                                                0
                                            ) ? undefined : (
                                                <BadgeDot color="primary">
                                                    {filtersCount}
                                                </BadgeDot>
                                            )
                                        }
                                    >
                                        {d.dataSource.name}
                                    </Tab>
                                    {datasets[datasets.length - 1].dataSource
                                        .id !== d.dataSource.id && (
                                        <Divider
                                            key={`${d.dataSource.id}-divider`}
                                            orientation="vertical"
                                            sx={{
                                                marginY: 1,
                                            }}
                                        />
                                    )}
                                </>
                            );
                        })
                    )}
                </TabGroup>
            </HorizontalScroller>

            <DatasetTableContainer
                mainTab={tab}
                data={dataset}
                channelId={channelId}
                tableId={tableId ?? ""}
                geospatialSelection={geospatialSelection}
                onGoToClick={onGoToClick}
                updateDataset={(filters) =>
                    onDatasetUpdate({
                        currentDataset: dataset,
                        i: filters,
                    })
                }
                client={client}
                hasTransitioned={hasTransitioned}
            />
        </Stack>
    );
};
